import React, { useState, useEffect } from 'react';
import './Login.css';
import { login } from '../../firebase/authService';

const MAX_ATTEMPTS = 5;
const LOCK_DURATION = 15 * 60 * 1000; // 15 دقيقة
const STORAGE_KEY = 'login_attempts';

function getAttemptsData() {
  try {
    return JSON.parse(localStorage.getItem(STORAGE_KEY)) || { count: 0, lockedUntil: null };
  } catch {
    return { count: 0, lockedUntil: null };
  }
}

function saveAttemptsData(data) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
}

function resetAttempts() {
  localStorage.removeItem(STORAGE_KEY);
}

function Login({ onLoginSuccess }) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [lockedUntil, setLockedUntil] = useState(null);
  const [remainingTime, setRemainingTime] = useState(0);
  const [attemptsLeft, setAttemptsLeft] = useState(MAX_ATTEMPTS);

  useEffect(() => {
    const data = getAttemptsData();
    if (data.lockedUntil && Date.now() < data.lockedUntil) {
      setLockedUntil(data.lockedUntil);
    } else if (data.lockedUntil && Date.now() >= data.lockedUntil) {
      resetAttempts();
    } else {
      setAttemptsLeft(MAX_ATTEMPTS - (data.count || 0));
    }
  }, []);

  useEffect(() => {
    if (!lockedUntil) return;
    const interval = setInterval(() => {
      const remaining = lockedUntil - Date.now();
      if (remaining <= 0) {
        setLockedUntil(null);
        setRemainingTime(0);
        setAttemptsLeft(MAX_ATTEMPTS);
        resetAttempts();
        clearInterval(interval);
      } else {
        setRemainingTime(remaining);
      }
    }, 1000);
    return () => clearInterval(interval);
  }, [lockedUntil]);

  const formatTime = (ms) => {
    const minutes = Math.floor(ms / 60000);
    const seconds = Math.floor((ms % 60000) / 1000);
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (lockedUntil) return;

    setError('');
    setLoading(true);

    try {
      await login(email, password);
      resetAttempts();
      onLoginSuccess();
    } catch (error) {
      const data = getAttemptsData();
      const newCount = (data.count || 0) + 1;

      if (newCount >= MAX_ATTEMPTS) {
        const lockTime = Date.now() + LOCK_DURATION;
        saveAttemptsData({ count: newCount, lockedUntil: lockTime });
        setLockedUntil(lockTime);
        setRemainingTime(LOCK_DURATION);
        setError('');
      } else {
        saveAttemptsData({ count: newCount, lockedUntil: null });
        setAttemptsLeft(MAX_ATTEMPTS - newCount);

        if (error.code === 'auth/invalid-credential' || error.code === 'auth/wrong-password') {
          setError('البريد الإلكتروني أو كلمة المرور غير صحيحة');
        } else if (error.code === 'auth/user-not-found') {
          setError('المستخدم غير موجود');
        } else if (error.code === 'auth/invalid-email') {
          setError('البريد الإلكتروني غير صالح');
        } else if (error.code === 'auth/too-many-requests') {
          setError('تم تجاوز عدد المحاولات. حاول مرة أخرى لاحقاً');
        } else {
          setError('حدث خطأ في تسجيل الدخول');
        }
      }
    } finally {
      setLoading(false);
    }
  };

  if (lockedUntil) {
    return (
      <div className="login-container">
        <div className="login-box">
          <div className="login-header">
            <h1>🔒 تم القفل مؤقتاً</h1>
            <p>تم تجاوز الحد المسموح به من المحاولات</p>
          </div>
          <div className="lockout-message">
            <p>يمكنك المحاولة مجدداً بعد:</p>
            <div className="lockout-timer">{formatTime(remainingTime)}</div>
            <p className="lockout-hint">دقيقة : ثانية</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="login-container">
      <div className="login-box">
        <div className="login-header">
          <h1>🔐 لوحة التحكم</h1>
          <p>سلسلة الفوائد - الشيخ سلمان الحران</p>
        </div>

        <form onSubmit={handleSubmit} className="login-form">
          {error && (
            <div className="error-message">
              ❌ {error}
              {attemptsLeft < MAX_ATTEMPTS && (
                <span className="attempts-warning"> (متبقي {attemptsLeft} محاولات)</span>
              )}
            </div>
          )}

          <div className="form-group">
            <label htmlFor="email">البريد الإلكتروني</label>
            <input
              type="email"
              id="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="admin@example.com"
              required
              disabled={loading}
            />
          </div>

          <div className="form-group">
            <label htmlFor="password">كلمة المرور</label>
            <input
              type="password"
              id="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="••••••••"
              required
              disabled={loading}
            />
          </div>

          <button
            type="submit"
            className="login-button"
            disabled={loading}
          >
            {loading ? (
              <>
                <span className="spinner"></span>
                جاري تسجيل الدخول...
              </>
            ) : (
              'تسجيل الدخول'
            )}
          </button>
        </form>

        <div className="login-footer">
          <p>⚠️ هذه الصفحة مخصصة للمسؤولين فقط</p>
        </div>
      </div>
    </div>
  );
}

export default Login;
